/** @jsxImportSource @emotion/react */
// React/core
import React, { useState } from 'react'
// Third-party
import { css } from '@emotion/react'
import { useHistory, useParams } from 'react-router-dom'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Icon from 'src/components/Icon'
import SchedulerErrorBoundary from './components/SchedulerErrorBoundary'
import { PublishScheduleModal } from './components'
import type { PublishDivision } from './components'
import { useSchedulerCompetition } from './hooks/useSchedulerCompetition'

interface RouteParams {
  competitionId: string
}

const PreviewPage: React.FC = () => {
  const history = useHistory()
  const { competitionId } = useParams<RouteParams>()
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)
  const { competition } = useSchedulerCompetition(competitionId)

  const competitionName = competition?.name || competition?.name_full || ''
  const leagueName = competition?.organization?.name || ''
  const competitionImage = competition?.image?.full_path || undefined

  // TODO: Replace with actual data from API
  const publishDivisions: PublishDivision[] = []

  const handleBack = () => {
    history.push(`/scheduler-wizard/${competitionId}/configure`)
  }

  const handleCompetitionEdit = () => {
    // TODO: Implement competition edit
  }

  const handleCancel = () => {
    history.push(`/scheduler-wizard/${competitionId}/configure`)
  }

  const handleSave = () => {
    // TODO: Implement save functionality
  }

  const handleCalendarView = () => {
    // TODO: Implement calendar view toggle
  }

  const handlePublishSchedule = () => {
    setIsPublishModalOpen(true)
  }

  const handlePublishModalClose = () => {
    setIsPublishModalOpen(false)
  }

  const handlePublishConfirm = () => {
    // TODO: Implement actual publish API call
    setIsPublishModalOpen(false)
  }

  const pageContainerStyles = css`
    min-height: 100vh;
    background-color: ${colors.SECONDARY_400};
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 88px;
  `

  const headerContainerStyles = css`
    max-width: 1440px;
    width: 100%;
    padding: 16px 0;
  `

  const headerInnerStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `

  const leftSectionStyles = css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `

  const backLinkStyles = css`
    color: ${colors.NEUTRAL_50};
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1.44px;
    text-transform: uppercase;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    padding: 0;
    line-height: 14.4px;
    transition: color 0.2s ease;

    &:hover {
      color: ${colors.NEUTRAL_70};
    }
  `

  const competitionRowStyles = css`
    display: flex;
    align-items: center;
    gap: 16px;
  `

  const competitionImageStyles = css`
    width: 64px;
    height: 64px;
    border-radius: 8px;
    object-fit: cover;
  `

  const competitionInfoStyles = css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `

  const titleRowStyles = css`
    display: flex;
    align-items: center;
    gap: 8px;
  `

  const titleStyles = css`
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 48px;
    font-weight: 600;
    color: ${colors.WHITE};
    margin: 0;
    line-height: 48px;
  `

  const editButtonStyles = css`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `

  const leagueNameStyles = css`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1.44px;
    text-transform: uppercase;
    color: ${colors.NEUTRAL_50};
    line-height: 14.4px;
    margin: 0;
  `

  const actionButtonsStyles = css`
    display: flex;
    align-items: center;
    gap: 16px;
  `

  const cancelButtonStyles = css`
    padding: 8px 16px;
    background-color: transparent;
    border: 2px solid ${colors.ERROR_200};
    color: ${colors.ERROR_200};
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 23.2px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: ${colors.ERROR_200};
      color: ${colors.WHITE};
    }
  `

  const saveButtonStyles = css`
    padding: 8px 16px;
    background-color: ${colors.INFO_400};
    border: none;
    color: ${colors.WHITE};
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 23.2px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: ${colors.INFO_300};
    }
  `

  const calendarButtonStyles = css`
    padding: 8px 16px;
    background-color: transparent;
    border: 2px solid ${colors.PRIMARY_300};
    color: ${colors.WHITE};
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 23.2px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;

    &:hover {
      background-color: ${colors.PRIMARY_300};
      color: ${colors.SECONDARY_300};
    }
  `

  const publishButtonStyles = css`
    padding: 8px 16px;
    background-color: ${colors.PRIMARY_300};
    border: none;
    color: ${colors.SECONDARY_300};
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 23.2px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;

    &:hover {
      background-color: ${colors.PRIMARY_200};
    }
  `

  return (
    <SchedulerErrorBoundary>
      <div css={pageContainerStyles}>
        <div css={headerContainerStyles}>
          <div css={headerInnerStyles}>
            {/* Left Section */}
            <div css={leftSectionStyles}>
              <button css={backLinkStyles} onClick={handleBack} type='button'>
                <Icon
                  name='chevron-left'
                  fontSize={16}
                  color={colors.NEUTRAL_50}
                />
                BACK
              </button>
              <div css={competitionRowStyles}>
                <img
                  css={competitionImageStyles}
                  src={competitionImage}
                  alt={competitionName}
                />
                <div css={competitionInfoStyles}>
                  <div css={titleRowStyles}>
                    <h1 css={titleStyles}>{competitionName}</h1>
                    <button
                      css={editButtonStyles}
                      onClick={handleCompetitionEdit}
                      type='button'
                      aria-label='Edit competition'
                    >
                      <Icon
                        name='pen'
                        fontSize={18}
                        color={colors.PRIMARY_300}
                      />
                    </button>
                  </div>
                  <p css={leagueNameStyles}>{leagueName}</p>
                </div>
              </div>
            </div>

            {/* Right Section - Action Buttons */}
            <div css={actionButtonsStyles}>
              <button
                css={cancelButtonStyles}
                onClick={handleCancel}
                type='button'
              >
                Cancel
              </button>
              <button css={saveButtonStyles} onClick={handleSave} type='button'>
                Save
              </button>
              <button
                css={calendarButtonStyles}
                onClick={handleCalendarView}
                type='button'
              >
                <Icon name='calendar' fontSize={24} color={colors.WHITE} />
                Calendar View
              </button>
              <button
                css={publishButtonStyles}
                onClick={handlePublishSchedule}
                type='button'
              >
                <Icon name='magic' fontSize={24} color={colors.SECONDARY_300} />
                Publish Schedule
              </button>
            </div>
          </div>
        </div>

        {/* Body content removed for refactoring */}
      </div>

      <PublishScheduleModal
        isOpen={isPublishModalOpen}
        onClose={handlePublishModalClose}
        onPublish={handlePublishConfirm}
        totalGames={0}
        divisions={publishDivisions}
      />
    </SchedulerErrorBoundary>
  )
}

export default PreviewPage
